import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { signInAdmin, requestPasswordReset, updatePassword } from '../../services/adminAuthService';

const lightTheme = {
  mode: 'light',
  bg: '#FAFAF7',
  surface: '#FFFFFF',
  ink: '#1A1F2B',
  inkSoft: '#3D434F',
  muted: '#6B7280',
  line: '#E8E5DE',
  gold: '#B8935A',
  goldSoft: '#F2EBDD',
  danger: '#9A3B3B',
  ok: '#3B7A3B',
  shadow: 'rgba(0,0,0,.08)',
};

const darkTheme = {
  mode: 'dark',
  bg: '#0F1117',
  surface: '#181B23',
  ink: '#F1ECE2',
  inkSoft: '#C9C4B8',
  muted: '#8A8F9A',
  line: '#262A36',
  gold: '#C9A06A',
  goldSoft: 'rgba(201,160,106,.14)',
  danger: '#C5544A',
  ok: '#7FB87F',
  shadow: 'rgba(0,0,0,.5)',
};

const THEME_KEY = 'mf_admin_v2_theme';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  background: ${p => p.theme.bg};
  color: ${p => p.theme.ink};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, ${p => p.theme.goldSoft} 0%, transparent 40%),
      radial-gradient(circle at 80% 80%, ${p => p.theme.goldSoft} 0%, transparent 45%);
    opacity: ${p => p.theme.mode === 'dark' ? .35 : .6};
    pointer-events: none;
  }
`;

const Card = styled.form`
  width: 100%;
  max-width: 420px;
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 14px;
  padding: 44px 40px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  box-shadow: 0 24px 60px ${p => p.theme.shadow};
  position: relative;
  z-index: 1;
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 2px;
    background: ${p => p.theme.gold};
  }
`;

const LogoBox = styled.div`
  margin-bottom: 4px;
  img {
    height: 56px;
    width: auto;
    object-fit: contain;
    display: block;
  }
`;

const Subtitle = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 18px;
  color: ${p => p.theme.inkSoft};
  letter-spacing: 1px;
  font-style: italic;
  margin-bottom: 12px;
`;

const Field = styled.label`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 10px;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: ${p => p.theme.muted};
  font-weight: 500;
  input {
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 14px;
    font-family: inherit;
    letter-spacing: 0.2px;
    text-transform: none;
    font-weight: 400;
    outline: none;
    transition: border-color .15s, box-shadow .15s;
    &:focus {
      border-color: ${p => p.theme.gold};
      box-shadow: 0 0 0 3px ${p => p.theme.goldSoft};
    }
    &:disabled { opacity: .6; cursor: not-allowed; }
  }
`;

const Message = styled.div`
  width: 100%;
  font-size: 12px;
  color: ${p => p.$ok ? p.theme.ok : p.theme.danger};
  text-align: center;
  letter-spacing: 0.2px;
  line-height: 1.4;
`;

const SubmitBtn = styled.button`
  width: 100%;
  margin-top: 10px;
  background: ${p => p.theme.ink};
  color: ${p => p.theme.bg};
  border: 1px solid ${p => p.theme.ink};
  padding: 13px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;
  transition: transform .15s, box-shadow .15s;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 20px ${p => p.theme.shadow}; }
  &:disabled { opacity: .6; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const LinkBtn = styled.button`
  background: transparent;
  border: none;
  color: ${p => p.theme.muted};
  font-size: 11px;
  letter-spacing: 0.5px;
  cursor: pointer;
  padding: 4px 8px;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-family: inherit;
  &:hover { color: ${p => p.theme.gold}; }
`;

const Footer = styled.div`
  margin-top: 6px;
  font-size: 10px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${p => p.theme.muted};
  text-align: center;
`;

const ModeToggle = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  color: ${p => p.theme.ink};
  cursor: pointer;
  font-size: 16px;
  z-index: 2;
  transition: transform .2s;
  &:hover { transform: rotate(15deg); }
`;

// mode: 'login' | 'forgot' | 'recover'
// 'recover' aparece quando o Supabase redireciona com ?recover=1 (após esqueci senha ou invite)
function detectInitialMode() {
  if (typeof window === 'undefined') return 'login';
  const params = new URLSearchParams(window.location.search);
  if (params.get('recover') === '1') return 'recover';
  // Supabase coloca o type no hash quando o magic link é resolvido
  if (window.location.hash.includes('type=recovery') || window.location.hash.includes('type=invite')) return 'recover';
  return 'login';
}

export default function LoginGateV2({ onAuth }) {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newPass2, setNewPass2] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(detectInitialMode);
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  });
  const themeObj = theme === 'dark' ? darkTheme : lightTheme;
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem(THEME_KEY, next);
  };

  const resetMessages = () => { setError(''); setInfo(''); };

  const handleLogin = async (e) => {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    const res = await signInAdmin(email, pass);
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    onAuth();
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    resetMessages();
    if (!email.trim()) { setError('Informe o email primeiro.'); return; }
    setBusy(true);
    const res = await requestPasswordReset(email);
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    setInfo('Email de recuperação enviado. Confira sua caixa de entrada.');
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    resetMessages();
    if (newPass.length < 8) { setError('Senha deve ter pelo menos 8 caracteres.'); return; }
    if (newPass !== newPass2) { setError('As senhas não coincidem.'); return; }
    setBusy(true);
    const res = await updatePassword(newPass);
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    setInfo('Senha atualizada. Faça login com a nova senha.');
    // limpa hash/query e volta pro login
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', window.location.pathname);
    }
    setNewPass(''); setNewPass2(''); setPass('');
    setTimeout(() => setMode('login'), 1500);
  };

  const logoSrc = theme === 'dark'
    ? '/mf-real-state/logos/mf-logo-dark.png'
    : '/mf-real-state/logos/mf-logo-light.png';

  let onSubmit = handleLogin;
  if (mode === 'forgot') onSubmit = handleForgot;
  if (mode === 'recover') onSubmit = handleRecover;

  return (
    <ThemeProvider theme={themeObj}>
      <Page>
        <ModeToggle
          type="button"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
          aria-label="Alternar tema"
        >
          {theme === 'dark' ? '☀' : '☾'}
        </ModeToggle>
        <Card onSubmit={onSubmit}>
          <LogoBox>
            <img src={logoSrc} alt="MF Imóveis" />
          </LogoBox>
          <Subtitle>
            {mode === 'login' && 'Painel administrativo'}
            {mode === 'forgot' && 'Recuperar senha'}
            {mode === 'recover' && 'Definir nova senha'}
          </Subtitle>

          {(mode === 'login' || mode === 'forgot') && (
            <Field>
              Email
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => { setEmail(e.target.value); resetMessages(); }}
                disabled={busy}
                autoFocus
              />
            </Field>
          )}

          {mode === 'login' && (
            <Field>
              Senha
              <input
                type="password"
                autoComplete="current-password"
                value={pass}
                onChange={e => { setPass(e.target.value); resetMessages(); }}
                disabled={busy}
              />
            </Field>
          )}

          {mode === 'recover' && (
            <>
              <Field>
                Nova senha
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPass}
                  onChange={e => { setNewPass(e.target.value); resetMessages(); }}
                  disabled={busy}
                  autoFocus
                />
              </Field>
              <Field>
                Confirmar nova senha
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPass2}
                  onChange={e => { setNewPass2(e.target.value); resetMessages(); }}
                  disabled={busy}
                />
              </Field>
            </>
          )}

          {error && <Message>{error}</Message>}
          {info && <Message $ok>{info}</Message>}

          <SubmitBtn type="submit" disabled={busy}>
            {busy && 'Aguarde...'}
            {!busy && mode === 'login' && 'Entrar'}
            {!busy && mode === 'forgot' && 'Enviar link'}
            {!busy && mode === 'recover' && 'Atualizar senha'}
          </SubmitBtn>

          {mode === 'login' && (
            <LinkBtn type="button" onClick={() => { resetMessages(); setMode('forgot'); }}>
              Esqueci minha senha
            </LinkBtn>
          )}
          {mode === 'forgot' && (
            <LinkBtn type="button" onClick={() => { resetMessages(); setMode('login'); }}>
              Voltar ao login
            </LinkBtn>
          )}

          <Footer>MF Real Estate · Alphaville</Footer>
        </Card>
      </Page>
    </ThemeProvider>
  );
}
